import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, retry, tap, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface CloudItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mime_type?: string;
  size?: number;
  modified_at?: string;
}

export interface CloudImportResult {
  imported: number;
  skipped: number;
  folders_tracked: number;
}

export interface FilePreviewInfo {
  url: string;
  file_type: string;
  file_name: string;
  file_size?: number;
}

export interface IBucket {
  id: string;
  name: string;
  description: string;
  created_at?: string;
  updated_at?: string;
  /**
   * Set when the folder is filled by the Moodle/ILIAS course sync. Such a
   * folder has no stored description; while it stays empty the lists show
   * `pages.knowledgeBase.syncFolderDescription.<source>` in the user's
   * language (#237).
   */
  lms_source?: 'moodle' | 'ilias' | null;
}

/**
 * Internal system folders (`__dozent_knowledge__`, …) carry a `__` prefix.
 * One rule for every list that shows knowledge bases (#224, #230):
 * - content pickers (Creator, exercise dialog) and the Wissensbasis page leave
 *   them out — they are not the customer's folders and appear once per
 *   lecturer; the lecturer manages theirs under Dozent › FAQ / Wissen › Dateien;
 * - agent link pickers (Agent › Wissen, placements) keep them, because the
 *   lecturer's files reach an agent only through such a link — there they are
 *   labelled „Dozenten-Dateien · <Name>" (`KnowledgeSourceNamePipe`).
 * Works for buckets and data sources alike (both carry the folder `name`).
 */
export function isSystemBucket(bucket: Pick<IBucket, 'name'>): boolean {
  return (bucket.name ?? '').startsWith('__');
}

interface FileMetadata {
  // Define the structure of file_metadata if known, otherwise leave it as an empty object
}

export interface IWebsite {
  id: string;
  client_id: string;
  datatype_id: string;
  url: string;
  cleaned_domain: string;
  url_type: string;
  refresh_cycle: string;
  website_metadata: {
    url: string;
    title: string;
    domain: string;
    bucket_ids: string[];
    datatype_id: string;
    retrieval_date: string;
    collection_name: string;
    datasource_type: string;
  };
  status: string;
  created_at: string;
  updated_at: string;
}

/** Creator content published into a bucket (al-filemanager creator_kb_ops.bucket_entries). */
export interface ICreatorKbEntry {
  datatype_id: string;
  name: string;
  agent_enabled: boolean;
  version_label?: string | null;
  creator_item_id?: string | null;
  creator_kind?: string | null;
  version_no?: number | null;
  status?: string | null;
  published_at?: string | null;
  outdated: boolean;
}

export interface ICourse {
  id: string;
  course_name: string;
  description: string;
  source_type: string;
  sync_frequency?: string;
  sections_count: number;
  items_count: number;
  created_at: string;
}

export interface IFile {
  id: number;
  datatype_id: string;
  client_id: number;
  collection_name: string;
  course_id: number | null;
  embedding_required: boolean;
  file_directory: string;
  file_format: string;
  file_metadata: FileMetadata;
  file_name: string;
  file_size: number;
  file_status: string;
  original_course_id: number | null;
  status_embedding: boolean;
  embedding_error?: string | null;
  updated_at: string; // ISO 8601 date string
  uploaded_at: string; // ISO 8601 date string
  user_group: string | null;
  user_id: number;
  uuid: string;
  // Cloud sync fields
  cloud_source?: string;
  cloud_file_id?: string;
  sync_frequency?: string;
  last_synced_at?: string;
  next_sync_at?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BucketService {
  private baseUrl = `${environment.filesBackend}`;

  _buckets = new BehaviorSubject<IBucket[]>([]);
  _files = new BehaviorSubject<IFile[]>([]);
  _websites = new BehaviorSubject<IWebsite[]>([]);
  _courses = new BehaviorSubject<ICourse[]>([]);
  _creatorItems = new BehaviorSubject<ICreatorKbEntry[]>([]);
  loading$ = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  createBucket(bucket: IBucket): Promise<IBucket> {
    this.loading$.next(true);
    return new Promise((resolve, reject) => {
      this.http
        .post(`${this.baseUrl}/bucket`, { ...bucket })
        .subscribe((data: any) => {
          const createdBucket = data.data;
          this._buckets.next([...this._buckets.value, createdBucket]);
          this.loading$.next(false);
          resolve(createdBucket);
        });
    });
  }

  updateBucket(bucket: IBucket): Promise<IBucket> {
    this.loading$.next(true);
    return new Promise((resolve, reject) => {
      this.http
        .patch(`${this.baseUrl}/bucket/${bucket.id}`, { ...bucket })
        .pipe(map((data: any) => data.data))
        .subscribe((updatedBucket: IBucket) => {
          this._buckets.next(
            this._buckets.value.map((b) =>
              // Keep list-only fields (`lms_source`) the PATCH answer lacks.
              b.id === updatedBucket.id ? { ...b, ...updatedBucket } : b
            )
          );
          this.loading$.next(false);
          resolve(updatedBucket);
        });
    });
  }

  deleteBucket(bucket: IBucket): Promise<IBucket> {
    this.loading$.next(true);
    return new Promise((resolve, reject) => {
      this.http
        .delete(`${this.baseUrl}/bucket/${bucket.id}`)
        .subscribe((data: any) => {
          this._buckets.next(
            this._buckets.value.filter((b) => b.id !== bucket.id)
          );
          this._files.next([]);
          this.loading$.next(false);
          resolve(data);
        });
    });
  }

  deleteFile(file: IFile) {
    this.loading$.next(true);
    return new Promise((resolve, reject) => {
      this.http
        .delete(`${this.baseUrl}/file/${file.id}`)
        .subscribe((data: any) => {
          this._files.next(this._files.value.filter((f) => f.id !== file.id));
          this.loading$.next(false);
          resolve(data);
        });
    });
  }

  /** Wie `listBuckets`, gibt die Liste aber zurück (Academy-Erledigt-Prüfung). */
  fetchBuckets(): Observable<IBucket[]> {
    return this.http.get(`${this.baseUrl}/bucket`).pipe(
      map((data: any) => (data?.data ?? []) as IBucket[]),
      tap((list) => this._buckets.next(list)),
    );
  }

  listBuckets() {
    this.loading$.next(true);
    this.http.get(`${this.baseUrl}/bucket`).subscribe((data: any) => {
      this._buckets.next(data.data);
      this.loading$.next(false);
    });
  }

  getBucketEntries(bucketId: string) {
    this.loading$.next(true);
    return this.http
      .get(`${environment.filesBackend}/bucket/${bucketId}/entries`)
      .pipe(
        // Idempotent GET: retry twice with 2s/4s backoff instead of failing
        // straight through to every caller. Deliberately no aggressive retry
        // loop — hammering this endpoint amplified the 2026-07-24 outage.
        retry({ count: 2, delay: (_err, n) => timer(2000 * n) }),
        map((data: any) => data.data),
        tap((data: any) => {
          this._files.next(data.datatypes.FILE || []);
          this._websites.next(data.datatypes.WEBSITE || []);
          this._courses.next(data.datatypes.COURSE_CONTENT || []);
          this._creatorItems.next(data.datatypes.CREATOR_ITEM || []);
        })
      );
  }

  getFileProcessingStatus() {
    return this.http.get(
      `${environment.filesBackend}/process/recursive-urls/status`
    );
  }

  assignFileToBucket(bucketId: string, fileIds: number[]) {
    return new Promise((resolve, reject) => {
      this.http
        .post(`${this.baseUrl}/bucket/assign-files`, {
          bucket_id: bucketId,
          file_ids: fileIds,
        })
        .subscribe((data: any) => {
          resolve(data.data);
        });
    });
  }

  addFile(file: IFile) {
    this._files.next([...this._files.value, file]);
  }

  /**
   * Eine Datei vom Agenten ausnehmen (oder wieder freigeben) und/oder
   * etikettieren. Schlüssel ist die datatype_id — dieselbe Id, die jeder
   * Chunk trägt, also genau die, die der Suchfilter ausschließt.
   */
  setFileAgentAccess(
    datatypeId: string,
    payload: { agent_enabled?: boolean; version_label?: string }
  ) {
    return this.http.patch(
      `${environment.filesBackend}/datatypes/${datatypeId}/agent-access`,
      payload
    );
  }

  /**
   * Einen ganzen Kurs vom Agenten ausnehmen. Fächert serverseitig auf alle
   * Inhaltselemente des Kurses auf — ein Kurs ist selbst keine Datatype.
   */
  setCourseAgentAccess(courseId: string, agentEnabled: boolean) {
    return this.http.patch(
      `${environment.filesBackend}/courses/${courseId}/agent-access`,
      { agent_enabled: agentEnabled }
    );
  }

  /**
   * Creator-Inhalt aus der Wissensbasis nehmen — über die Creator-Route, damit
   * der Zielstand („Live in“) stimmt (creator-items.md). Läuft als Job.
   */
  withdrawCreatorItem(itemId: string, bucketId: string) {
    return this.http.delete<{ jobId: string }>(
      `${environment.filesBackend}/creator/items/${itemId}/kb/${bucketId}`
    );
  }

  deleteDatatype(datatypeIds: string[]) {
    this.loading$.next(true);
    return this.http
      .delete(`${environment.filesBackend}/datatypes`, {
        body: { datatype_ids: datatypeIds },
      })
      .pipe(
        map((data: any) => data.data),
        tap((data: any) => {
          const deletedIs = data.deleted.map((d: any) => d.datatype_id);
          const files = this._files.value.filter(
            (f) => !deletedIs.includes(f.datatype_id)
          );
          const websites = this._websites.value.filter(
            (w) => !deletedIs.includes(w.datatype_id)
          );

          this._files.next(files);
          this._websites.next(websites);
          this.loading$.next(false);
        })
      );
  }

  refreshWebsite(datatypeIds: string[]) {
    this.loading$.next(true);
    return this.http
      .post(`${environment.filesBackend}/website/refresh`, {
        datatype_ids: datatypeIds,
      })
      .pipe(
        map((data: any) => data.data),
        tap((data: any) => {
          this.loading$.next(false);
        })
      );
  }

  get buckets() {
    return this._buckets.asObservable();
  }

  get files() {
    return this._files.asObservable();
  }

  get websites() {
    return this._websites.asObservable();
  }

  get courses() {
    return this._courses.asObservable();
  }

  browseCloud(provider: string, folderId: string = 'root'): Observable<CloudItem[]> {
    return this.http.get<CloudItem[]>(
      `${this.baseUrl}/integrations/cloud/${provider}/browse`,
      { params: { folder_id: folderId } }
    );
  }

  importCloud(payload: {
    provider: string;
    items: CloudItem[];
    bucket_id?: string;
    sync_frequency: string;
  }): Observable<CloudImportResult> {
    return this.http.post<CloudImportResult>(
      `${this.baseUrl}/integrations/cloud/import`,
      payload
    );
  }

  getPreviewUrl(fileId: string): Observable<FilePreviewInfo> {
    return this.http.get<FilePreviewInfo>(`${this.baseUrl}/files/${fileId}/preview`);
  }

  private resetState() {
    this._buckets.next([]);
    this._files.next([]);
    this._websites.next([]);
    this._courses.next([]);
    this._creatorItems.next([]);
    this.loading$.next(false);
  }
}
